import { forwardRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type Option } from '@/types/select-option';
import { useTranslations } from 'next-intl';

interface MetadataSelectProps {
  'value': string;
  'onChange': (value: string) => void;
  'options': Array<Option>;
  'className'?: string;
  'disabled'?: boolean;
  'isLoading'?: boolean;
  'placeholder'?: string;
  'data-cy'?: string;
  'indeterminate'?: boolean;
  'name'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

export const MetadataSelect = forwardRef<HTMLButtonElement, MetadataSelectProps>(
  (
    {
      onChange,
      options,
      className,
      disabled,
      isLoading,
      placeholder,
      value,
      indeterminate,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const tCommon = useTranslations('common');

    const handleChange = (selectedValue: string) => {
      onChange(selectedValue);
    };

    // `name` goes on the root as well as the trigger: Radix passes the root's `name` to the hidden
    // native <select> it renders for form participation, and without it the browser reports "A form
    // field element should have an id or name attribute" for that control.
    return (
      <Select
        key={value}
        name={props.name}
        onValueChange={handleChange}
        disabled={disabled ?? isLoading}
        value={value || ''}
      >
        <SelectTrigger
          ref={ref}
          className={className}
          aria-invalid={ariaInvalid}
          {...(props['data-cy'] ? { 'data-cy': `${props['data-cy']}-trigger` } : {})}
          indeterminate={indeterminate}
          name={props.name}
          isLoading={isLoading}
        >
          <SelectValue placeholder={placeholder ?? tCommon('selectAnOption')} />
        </SelectTrigger>
        <SelectContent
          {...(props['data-cy'] ? { 'data-cy': `${props['data-cy']}-dropdown` } : {})}
          className="max-w-(--radix-select-trigger-width)"
        >
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {/* `wrap-anywhere` sits on the text itself, not on the item or a wrapper: a metadata
                  value can be one unbroken run of characters, and any flex box between the item and
                  the text is a flex child at `min-width: auto` that will not shrink to let it wrap.
                  Hence also no wrapper at all unless there is a description to stack under it. */}
              {option.description ? (
                <div className="flex flex-col">
                  <span className="min-w-0 wrap-anywhere">{option.label}</span>
                  {option.description && <span className="text-xs text-text-gray-alternate">{option.description}</span>}
                </div>
              ) : (
                <span className="min-w-0 wrap-anywhere">{option.label}</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);

MetadataSelect.displayName = 'MetadataSelect';
